import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Github, 
  MessageSquare, 
  Mail, 
  Calendar, 
  HardDrive, 
  Trello,
  Slack,
  Plus,
  Settings,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Key,
  Shield,
  Activity,
  BarChart3,
  Users,
  FileText,
  Clock,
  Link2
} from 'lucide-react';
import { toast } from 'sonner';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  category: 'development' | 'communication' | 'productivity' | 'analytics';
  isConnected: boolean;
  isActive: boolean;
  config: {
    apiKey?: string;
    webhookUrl?: string;
    syncInterval?: number;
    autoSync?: boolean;
    permissions?: string[];
  };
  features: string[];
  lastSync?: Date;
  syncStatus: 'success' | 'error' | 'pending';
  stats: {
    itemsSynced: number;
    lastActivity: Date;
    errorCount: number;
  };
}

const availableIntegrations: Integration[] = [
  {
    id: 'github',
    name: 'GitHub',
    description: 'Sincronize repositórios, issues e pull requests',
    icon: <Github className="w-6 h-6" />,
    color: 'bg-gray-900',
    category: 'development',
    isConnected: true,
    isActive: true,
    config: {
      apiKey: '***hidden***',
      syncInterval: 30,
      autoSync: true,
      permissions: ['repo', 'issues', 'pull_requests']
    },
    features: [
      'Sincronizar issues como tarefas',
      'Atualizar status de pull requests',
      'Criar branches automaticamente',
      'Integração com commits'
    ],
    lastSync: new Date(Date.now() - 10 * 60 * 1000),
    syncStatus: 'success',
    stats: {
      itemsSynced: 47,
      lastActivity: new Date(Date.now() - 5 * 60 * 1000),
      errorCount: 0
    }
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Notificações e atualizações em tempo real',
    icon: <Slack className="w-6 h-6" />,
    color: 'bg-purple-600',
    category: 'communication',
    isConnected: true,
    isActive: true,
    config: {
      webhookUrl: 'https://hooks.slack.com/services/***',
      autoSync: true,
      permissions: ['channels:read', 'chat:write', 'users:read']
    },
    features: [
      'Notificações de tarefas',
      'Atualizações de projeto',
      'Comandos slash',
      'Integração com canais'
    ],
    lastSync: new Date(Date.now() - 2 * 60 * 1000),
    syncStatus: 'success',
    stats: {
      itemsSynced: 156,
      lastActivity: new Date(Date.now() - 1 * 60 * 1000),
      errorCount: 2
    }
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    description: 'Gerencie arquivos e documentos do projeto',
    icon: <HardDrive className="w-6 h-6" />,
    color: 'bg-blue-500',
    category: 'productivity',
    isConnected: false,
    isActive: false,
    config: {
      syncInterval: 60,
      autoSync: false,
      permissions: ['drive.file', 'drive.readonly']
    },
    features: [
      'Anexar arquivos às tarefas',
      'Sincronizar documentos',
      'Backup automático',
      'Compartilhamento de arquivos'
    ],
    syncStatus: 'pending',
    stats: {
      itemsSynced: 0,
      lastActivity: new Date(),
      errorCount: 0
    }
  },
  {
    id: 'trello',
    name: 'Trello',
    description: 'Importe boards e cards existentes',
    icon: <Trello className="w-6 h-6" />,
    color: 'bg-blue-600',
    category: 'productivity',
    isConnected: false,
    isActive: false,
    config: {
      apiKey: '',
      syncInterval: 120,
      autoSync: false
    },
    features: [
      'Importar boards como projetos',
      'Sincronizar cards como tarefas',
      'Manter histórico de atividades',
      'Migração de dados'
    ],
    syncStatus: 'pending',
    stats: {
      itemsSynced: 0,
      lastActivity: new Date(),
      errorCount: 0
    }
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Sincronize prazos e reuniões',
    icon: <Calendar className="w-6 h-6" />,
    color: 'bg-green-500',
    category: 'productivity',
    isConnected: true,
    isActive: false,
    config: {
      syncInterval: 15,
      autoSync: false,
      permissions: ['calendar.readonly', 'calendar.events']
    },
    features: [
      'Prazos como eventos',
      'Reuniões de projeto',
      'Lembretes automáticos',
      'Sincronização bidirecional'
    ],
    lastSync: new Date(Date.now() - 30 * 60 * 1000),
    syncStatus: 'error',
    stats: {
      itemsSynced: 23,
      lastActivity: new Date(Date.now() - 60 * 60 * 1000),
      errorCount: 5
    }
  },
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Envio automático de relatórios e notificações',
    icon: <Mail className="w-6 h-6" />,
    color: 'bg-red-500',
    category: 'communication',
    isConnected: false,
    isActive: false,
    config: {
      permissions: ['gmail.send', 'gmail.readonly']
    },
    features: [
      'Relatórios por email',
      'Notificações personalizadas',
      'Conversão de emails em tarefas',
      'Templates automáticos'
    ],
    syncStatus: 'pending',
    stats: {
      itemsSynced: 0,
      lastActivity: new Date(),
      errorCount: 0
    }
  }
];

export const Integrations: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>(availableIntegrations);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { id: 'all', label: 'Todas', icon: <Link2 className="w-4 h-4" /> },
    { id: 'development', label: 'Desenvolvimento', icon: <Github className="w-4 h-4" /> },
    { id: 'communication', label: 'Comunicação', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'productivity', label: 'Produtividade', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'analytics', label: 'Analytics', icon: <Activity className="w-4 h-4" /> }
  ];

  const filteredIntegrations = integrations.filter(integration => 
    selectedCategory === 'all' || integration.category === selectedCategory
  );

  const connectedIntegrations = integrations.filter(i => i.isConnected);
  const activeIntegrations = integrations.filter(i => i.isActive);

  const handleToggleConnection = (integrationId: string) => {
    setIntegrations(prev => prev.map(integration => 
      integration.id === integrationId 
        ? { ...integration, isConnected: !integration.isConnected }
        : integration
    ));
    
    const integration = integrations.find(i => i.id === integrationId);
    toast.success(
      `${integration?.name} ${integration?.isConnected ? 'desconectado' : 'conectado'} com sucesso`
    );
  };

  const handleToggleActive = (integrationId: string) => {
    setIntegrations(prev => prev.map(integration => 
      integration.id === integrationId 
        ? { ...integration, isActive: !integration.isActive }
        : integration
    ));
    
    const integration = integrations.find(i => i.id === integrationId);
    toast.success(
      `${integration?.name} ${integration?.isActive ? 'desativado' : 'ativado'}`
    );
  };

  const handleSync = (integrationId: string) => {
    setIntegrations(prev => prev.map(integration => 
      integration.id === integrationId 
        ? { ...integration, syncStatus: 'pending' as const, lastSync: new Date() }
        : integration
    ));

    // Simulate sync process
    setTimeout(() => {
      setIntegrations(prev => prev.map(integration => 
        integration.id === integrationId 
          ? { 
              ...integration, 
              syncStatus: 'success' as const,
              stats: {
                ...integration.stats,
                itemsSynced: integration.stats.itemsSynced + Math.floor(Math.random() * 10) + 1,
                lastActivity: new Date()
              }
            }
          : integration
      ));
      toast.success('Sincronização concluída com sucesso');
    }, 2000);
  };

  const handleConfigure = (integration: Integration) => {
    setSelectedIntegration(integration);
    setIsConfigDialogOpen(true);
  };

  const getSyncStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'pending': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getSyncStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'pending': return <RefreshCw className="w-4 h-4 text-yellow-500 animate-spin" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getSyncStatusText = (status: string) => {
    switch (status) {
      case 'success': return 'Sucesso';
      case 'error': return 'Erro';
      case 'pending': return 'Sincronizando...';
      default: return 'Aguardando';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Integrações</h1>
          <p className="text-gray-600 mt-1">Conecte suas ferramentas favoritas para maximizar a produtividade</p>
        </div>
        <Button variant="outline">
          <Plus className="w-4 h-4 mr-2" />
          Solicitar Integração
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Integrações Conectadas</p>
                <p className="text-2xl font-bold text-blue-600">{connectedIntegrations.length}</p>
              </div>
              <Link2 className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Integrações Ativas</p>
                <p className="text-2xl font-bold text-green-600">{activeIntegrations.length}</p>
              </div>
              <Activity className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Itens Sincronizados</p>
                <p className="text-2xl font-bold text-purple-600">
                  {integrations.reduce((sum, i) => sum + i.stats.itemsSynced, 0)}
                </p>
              </div>
              <RefreshCw className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Erros</p>
                <p className="text-2xl font-bold text-red-600">
                  {integrations.reduce((sum, i) => sum + i.stats.errorCount, 0)}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-2">
        {categories.map(category => (
          <Button
            key={category.id}
            variant={selectedCategory === category.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(category.id)}
            className="flex items-center gap-2"
          >
            {category.icon}
            {category.label}
          </Button>
        ))}
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredIntegrations.map(integration => (
          <Card key={integration.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${integration.color} text-white`}>
                    {integration.icon}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{integration.name}</CardTitle>
                    <Badge variant="secondary" className="mt-1">
                      {integration.category}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant={integration.isConnected ? 'default' : 'secondary'}>
                    {integration.isConnected ? 'Conectado' : 'Desconectado'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">{integration.description}</p>
              
              {/* Features */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Recursos:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {integration.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {integration.features.length > 3 && (
                    <li className="text-xs text-gray-500">
                      +{integration.features.length - 3} mais recursos
                    </li>
                  )}
                </ul>
              </div>

              {/* Stats */}
              {integration.isConnected && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Sincronizados:</span>
                    <div className="font-medium">{integration.stats.itemsSynced}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Erros:</span>
                    <div className="font-medium">{integration.stats.errorCount}</div>
                  </div>
                </div>
              )}

              {/* Sync Status */}
              {integration.isConnected && (
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    {getSyncStatusIcon(integration.syncStatus)}
                    <span className={getSyncStatusColor(integration.syncStatus)}>
                      {getSyncStatusText(integration.syncStatus)}
                    </span>
                  </div>
                  {integration.lastSync && (
                    <span className="text-gray-500">
                      {integration.lastSync.toLocaleTimeString('pt-BR')}
                    </span>
                  )}
                </div>
              )}

              {/* Controls */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center space-x-2">
                  {integration.isConnected && (
                    <Switch
                      checked={integration.isActive}
                      onCheckedChange={() => handleToggleActive(integration.id)}
                    />
                  )}
                  <span className="text-sm text-gray-600">
                    {integration.isConnected ? 'Ativo' : 'Conectar'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {integration.isConnected && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSync(integration.id)}
                      disabled={integration.syncStatus === 'pending'}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleConfigure(integration)}
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={integration.isConnected ? 'destructive' : 'default'}
                    size="sm"
                    onClick={() => handleToggleConnection(integration.id)}
                  >
                    {integration.isConnected ? 'Desconectar' : 'Conectar'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Configuration Dialog */}
      <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurar {selectedIntegration?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedIntegration && (
            <div className="space-y-6">
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                <div className={`p-2 rounded-lg ${selectedIntegration.color} text-white`}>
                  {selectedIntegration.icon}
                </div>
                <div>
                  <h3 className="font-medium">{selectedIntegration.name}</h3>
                  <p className="text-sm text-gray-600">{selectedIntegration.description}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <div className="flex space-x-2">
                    <Input
                      type="password"
                      value={selectedIntegration.config.apiKey || ''}
                      placeholder="Cole sua API key aqui..."
                    />
                    <Button variant="outline" size="sm">
                      <Key className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Intervalo de Sincronização</Label>
                  <Select value={selectedIntegration.config.syncInterval?.toString()}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutos</SelectItem>
                      <SelectItem value="30">30 minutos</SelectItem>
                      <SelectItem value="60">1 hora</SelectItem>
                      <SelectItem value="120">2 horas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch checked={selectedIntegration.config.autoSync} />
                  <Label>Sincronização automática</Label>
                </div>

                <div className="space-y-2">
                  <Label>Permissões</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedIntegration.config.permissions?.map(permission => (
                      <Badge key={permission} variant="secondary">
                        {permission}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsConfigDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button>
                  <Shield className="w-4 h-4 mr-2" />
                  Salvar Configurações
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};